import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ChatbotTypingIndicator from "./ChatbotTypingIndicator";
import { ChatDataType } from "../constant";
import ArrowUpChatIcon from "../../../../../Assets/Icons/arrow-up-chat.svg";
import ArrowUpChatDisabledIcon from "../../../../../Assets/Icons/arrow-up-chat-disabled.svg";
import ChatbotLogo from "../../../../../Assets/Icons/chatbot-logo.svg";
import Markdown from "react-markdown";
import ChatbotDatePicker from "./ChatbotDatePicker";
import { Skeleton } from "@mui/material";
import rehypeExternalLinks from "rehype-external-links";
import StarIcon from "../../../../../Assets/Icons/star.svg";
import ChatbotGreetingMessage from "./ChatbotGreetingMessage";

const CustomLoading = () => {
  const skeletonCount = 10;
  return (
    <>
      {[...Array(skeletonCount)].map((_, idx) => (
        <Skeleton key={idx} variant="rounded" height={40} />
      ))}
    </>
  );
};

const StreamingContent = ({ streamingContent, parsing }) => {
  const [userIsScrolling, setUserIsScrolling] = useState(false);
  const bottomRef = useRef(null);
  const contentRef = useRef(null);

  // Handle scroll events to detect when user is manually scrolling
  const handleScroll = useCallback(() => {
    if (!contentRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } =
      contentRef.current.closest(".chatbot-panel__main-content");
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 10; // tolerance of 10px

    if (!isAtBottom) {
      setUserIsScrolling(true);
    } else {
      setUserIsScrolling(false);
    }
  }, []);

  // Add scroll event listener
  useEffect(() => {
    const mainContent = document.querySelector(".chatbot-panel__main-content");
    if (mainContent) {
      mainContent.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (mainContent) {
        mainContent.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    if (bottomRef.current && !userIsScrolling) {
      // Only scroll to bottom if user is not manually scrolling up
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      });
    }
  }, [streamingContent, userIsScrolling]);

  const memoizedMarkdown = useMemo(
    () => (
      <Markdown
        rehypePlugins={[[rehypeExternalLinks, { target: "_blank" }]]}
        components={{
          p: ({ node, children, ...props }) => (
            <p
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              {...props}
            >
              {children}
              <span className="typing-indicator-span animate-pulse" />
            </p>
          ),
        }}
      >
        {streamingContent}
      </Markdown>
    ),
    [streamingContent]
  );

  return (
    <div className="bot-wrapper" ref={contentRef}>
      <div className="text bot">
        <img src={ChatbotLogo} alt="chatbot-logo" className="chatbot-logo" />
        <div id="streaming-content" className="break-word">
          {memoizedMarkdown}
        </div>
        {parsing && <div className="dot-flashing" />}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};

const ChatbotMainPanel = ({
  userIsGreeted,
  chatData,
  onSubmit,
  isWaitingResponse,
  loading,
  showSidePanel,
  isStreaming,
  streamingContent,
  onResetChat,
  parsing,
  userChatToken,
  isLoadingUserChatToken,
  disableSubmit,
}) => {
  const [currentChat, setCurrentChat] = useState("");
  const [showGreetingMessage, setShowGreetingMessage] = useState(false);

  const chatContentRef = useRef(null);
  const textAreaRef = useRef(null);

  const scrollToBottom = () => {
    if (chatContentRef.current) {
      chatContentRef.current.scrollTop = chatContentRef.current.scrollHeight;
    }
  };

  const handleTextareaChange = useCallback((e) => {
    setCurrentChat(e?.target?.value);

    // Use requestAnimationFrame for smoother performance
    handleTextareaSize();
  }, []);

  const handleTextareaSize = () => {
    requestAnimationFrame(() => {
      if (textAreaRef.current) {
        // Set minimum height first
        textAreaRef.current.style.height = "38px";
        // Then set to scrollHeight with a maximum
        textAreaRef.current.style.height = `${Math.max(
          56,
          Math.min(textAreaRef.current.scrollHeight, 150)
        )}px`;
      }
    });
  };

  // Scroll on new messages
  useEffect(() => {
    // Add a small delay to ensure the new message is rendered
    setTimeout(scrollToBottom);
  }, [chatData, isStreaming]);

  const renderContent = useCallback(
    (item) => {
      if (item.type === ChatDataType.Date) {
        return (
          <div className="date-wrapper">
            <div className="date">{dayjs(item.content).format("MMM DD")}</div>
          </div>
        );
      }

      if (item.type === ChatDataType.Text) {
        if (item.bot) {
          return (
            <div className="bot-wrapper">
              <div className="text bot break-word">
                <img
                  src={ChatbotLogo}
                  alt="chatbot-logo"
                  className="chatbot-logo"
                />
                <Markdown
                  rehypePlugins={[[rehypeExternalLinks, { target: "_blank" }]]}
                  components={{
                    p: ({ node, ...props }) => (
                      <p
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                        {...props}
                      />
                    ),
                  }}
                >
                  {item.content}
                </Markdown>
              </div>
            </div>
          );
        }

        return (
          <div className="user-wrapper">
            <div className="text user break-word">
              <Markdown
                rehypePlugins={[[rehypeExternalLinks, { target: "_blank" }]]}
                components={{
                  p: ({ node, ...props }) => (
                    <p
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                      {...props}
                    />
                  ),
                }}
              >
                {item.content}
              </Markdown>
            </div>
          </div>
        );
      }

      if (item.type === ChatDataType.Chips) {
        return (
          <div className="chips">
            {item.content.map((chip, index) => (
              <div key={index} className="chip" onClick={() => onSubmit(chip)}>
                {chip}
              </div>
            ))}
          </div>
        );
      }

      if (item.type === ChatDataType.DateInput) {
        return (
          <div className="date-input">
            <ChatbotDatePicker onSelect={onSubmit} />
          </div>
        );
      }
    },
    [onSubmit]
  );

  const isTokenLimitReached = userChatToken?.usage >= userChatToken?.maxAmount;
  const isDisableSubmit =
    !currentChat ||
    isWaitingResponse ||
    isStreaming ||
    isLoadingUserChatToken ||
    isTokenLimitReached ||
    disableSubmit;

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (isDisableSubmit) {
      return;
    }

    onSubmit(currentChat);
    setCurrentChat("");
    handleTextareaSize();
    setShowGreetingMessage(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  useEffect(() => {
    if (!userIsGreeted && !loading) {
      setShowGreetingMessage(true);
    }
  }, [userIsGreeted, loading]);

  return (
    <div className="chatbot-panel-container">
      <div
        className={`beta-capsule-container transition-all ${
          showSidePanel ? "double-panel" : "single-panel"
        }`}
      ></div>

      <div
        className={`chatbot-panel chatbot-panel__main transition-all ${
          showSidePanel ? "double-panel" : "single-panel"
        }`}
      >
        <div className="chatbot-panel__main-header">
          <img src={StarIcon} alt="star" />
          <div
            className="chatbot-panel__main-header-reset-chat"
            onClick={onResetChat}
          >
            Reset Agent
          </div>
        </div>

        <div className="chatbot-panel__main-content" ref={chatContentRef}>
          {showGreetingMessage && (
            <ChatbotGreetingMessage
              onSelect={(chip) => {
                onSubmit(chip);
                setCurrentChat("");
                handleTextareaSize();
                setShowGreetingMessage(false);
              }}
            />
          )}
          {!loading &&
            chatData?.map((item, index) => (
              <div key={index}>
                {renderContent(item)}

                {item.timestamp && (
                  <div
                    className={item.bot ? "timestamp-bot" : "timestamp-user"}
                  >
                    {dayjs(item.timestamp).format("h:mm A")}
                  </div>
                )}
              </div>
            ))}
          {loading && <CustomLoading />}
          {isWaitingResponse && <ChatbotTypingIndicator />}
          {isStreaming && (
            <StreamingContent
              streamingContent={streamingContent}
              parsing={parsing}
            />
          )}
        </div>

        <form className="chatbot-panel__main-chat" onSubmit={handleSubmit}>
          <textarea
            ref={textAreaRef}
            className="chatbot-panel__main-chat-input"
            placeholder="Message WeddinglyAi Agent..."
            value={currentChat}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
          />
          <button
            className="chatbot-panel__main-chat-button"
            type="submit"
            disabled={isDisableSubmit}
          >
            {isDisableSubmit ? (
              <img src={ArrowUpChatDisabledIcon} alt="arrow-up-chat" />
            ) : (
              <img src={ArrowUpChatIcon} alt="arrow-up-chat" />
            )}
          </button>
        </form>

        <div className="chatbot-panel__main-chat-footer">
          <span style={{ color: isTokenLimitReached ? "#B3261E" : "inherit" }}>
            {isLoadingUserChatToken ? (
              <Skeleton variant="rounded" height={14} width={150} />
            ) : (
              `${userChatToken?.usage?.toLocaleString(
                "en-US"
              )} of ${userChatToken?.maxAmount?.toLocaleString("en-US")} tokens`
            )}
          </span>

          <span>
            WeddinglyAi Agent provides suggestions and estimates only. Verify
            important details.
          </span>
        </div>
      </div>
    </div>
  );
};
export default ChatbotMainPanel;
